/**
 * The payments browser — `/v1/admin/payments`.
 *
 * **The server decides how much of a payment this admin sees.** `shows_instruments` comes back on
 * every response, and the method fields are simply absent when it is false. The console renders
 * "withheld" from that flag rather than inferring it from a missing field — an absent value could
 * equally mean the gateway never told us, and those are different facts.
 *
 * **No mutation lives here.** This is a read-only mirror of the gateway: no charge can be created
 * and no payment edited. Webhook replay is E15's, and it will arrive as its own hook rather than
 * being smuggled into this one.
 */
import { useQuery } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type ConsolePaymentRow = components['schemas']['ConsolePaymentRow'];
export type ConsoleTimelineEntry = components['schemas']['ConsoleTimelineEntry'];
type ConsolePaymentPage = components['schemas']['ConsolePaymentPage'];
type ConsolePaymentDetail = components['schemas']['ConsolePaymentDetail'];
type PaymentsHealth = components['schemas']['PaymentsHealth'];

export interface PaymentsQuery {
  tab?: string;
  status?: string;
  method?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/** Only what the operator set. An empty `q=` would narrow to nothing. */
export function paymentsParams(query: PaymentsQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function usePayments(query: PaymentsQuery) {
  const params = paymentsParams(query);
  return useQuery({
    queryKey: keys.payments.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => authedClient.request<ConsolePaymentPage>(`/v1/admin/payments?${params}`),
  });
}

export function usePaymentsHealth(hours = 24) {
  return useQuery({
    queryKey: keys.payments.health(hours),
    queryFn: () =>
      authedClient.request<PaymentsHealth>(`/v1/admin/payments/health?hours=${hours}`),
  });
}

export function usePaymentDetail(orderId: number | null) {
  return useQuery({
    queryKey: keys.payments.one(orderId ?? 0),
    queryFn: () => authedClient.request<ConsolePaymentDetail>(`/v1/admin/payments/${orderId}`),
    enabled: orderId !== null,
  });
}
